import { EntityId, PayloadAction } from "@reduxjs/toolkit"
import { addSeconds, isBefore } from "date-fns"
import { chunk } from "lodash"
import { SagaIterator } from "redux-saga"
import {
  all,
  call,
  delay,
  put,
  race,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects"
import {
  clearVNZTokensFromStorage,
  getVNZTokensFromStorage,
  saveVNZTokensToStorage,
  VNZService,
} from "~/components/cloud-provider/vnz/service/VNZService"
import { ApiException } from "~/interfaces/entities/ApiException"
import { VNZAuthTokenEntity } from "~/interfaces/entities/VNZAuthTokenEntity"
import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { VNZSessionImage } from "~/interfaces/entities/VNZSessionImage"
import { applyCouponToCart, getCurrentCart } from "~/redux/saga/cart"
import { scrollToCartOrStoreSearch } from "~/redux/saga/processLineItems"
import {
  vnzSessionImages,
  vnzSessionImagesSelectors,
} from "~/redux/state/VNZSessionImages"
import { logger } from "~/services/Logger"
import { PrinticularApi } from "~/services/PrinticularApi"
import { scrollTo, seemsToBeAnImage } from "~/services/Utils"
import { actions, startFetchingVNZCloudImages } from "../process"
import {
  RefreshAuthenticationTokenPayload,
  SetIsAuthenticatedPayload,
  VNZAuth,
} from "../state/VNZAuth"
import {
  LoadThumbnailForImagePayload,
  vnzImages,
  vnzImagesSelectors,
} from "../state/VNZImages"
import { RootState } from "../store"
import { getApi } from "./api"
import { getVnzService } from "./VNZService"

// Cap how many decoded thumbnail blobs are kept alive at once. Cells that scroll
// far out of view get their object URL revoked (freeing memory); the flags are
// reset so the thumbnail re-fetches if the cell is scrolled back into view. This
// keeps blob memory bounded even for libraries of thousands of images.
const THUMBNAIL_CACHE_LIMIT = 800
const loadedThumbnailIds: EntityId[] = []

export function* watchSaveVNZAuthentication() {
  yield takeEvery(
    VNZAuth.actions.setIsAuthenticated.type,
    saveVNZAuthentication
  )
}

function saveVNZAuthentication(
  action: PayloadAction<SetIsAuthenticatedPayload>
) {
  const { payload } = action
  saveVNZTokensToStorage(payload)
}

export function* watchStartVNZAccessTokenRefresher() {
  yield takeLatest(
    VNZAuth.actions.setIsAuthenticated.type,
    startVNZAccessTokenRefresher
  )
}

// refresh this long before the access token actually expires
const REFRESH_BUFFER_IN_SECONDS = 5 * 60
const REFRESH_BUFFER_IN_MS = REFRESH_BUFFER_IN_SECONDS * 1000
// floor for the refresh delay: after a transient refresh failure the expiry
// is close or past, this becomes the retry interval instead of a full cycle
const MINIMUM_REFRESH_DELAY_IN_MS = 30 * 1000

function* startVNZAccessTokenRefresher(): SagaIterator<void> {
  while (true) {
    const expiryDate: string = yield select(
      (state: RootState) => state.VNZAuth.expiryDate
    )
    const expiryTimeInMs = new Date(expiryDate).getTime()

    if (isNaN(expiryTimeInMs)) {
      // defensive: without a readable expiry the timer cannot be scheduled
      logger.error("VNZ access token refresher stopped: invalid expiry date")
      break
    }

    const delayInMs = Math.max(
      expiryTimeInMs - Date.now() - REFRESH_BUFFER_IN_MS,
      MINIMUM_REFRESH_DELAY_IN_MS
    )

    const { cancelled } = yield race({
      delayed: delay(delayInMs),
      cancelled: take(VNZAuth.actions.logout.type),
    })

    if (cancelled) {
      break
    }

    yield put(VNZAuth.actions.refreshAuthenticationToken({}))
  }
}

function getTokensFromLocalStorage(): {
  accessToken: string
  refreshToken: string
  expiresIn: number
  expiryDate: Date
  isValid: boolean
  hasRequiredData: boolean
} {
  const {
    accessToken,
    refreshToken,
    expiresIn,
    expiryDate: expiryDateString,
  } = getVNZTokensFromStorage()
  const expiryDate = expiryDateString ? new Date(expiryDateString) : new Date(0)

  const hasRequiredData = Boolean(accessToken && refreshToken && expiresIn)
  const isValid = hasRequiredData && isBefore(new Date(), expiryDate)

  return {
    accessToken,
    refreshToken,
    expiresIn,
    expiryDate,
    isValid,
    hasRequiredData,
  }
}

export function* watchRestoreVNZAuthenticationState() {
  yield takeEvery(
    VNZAuth.actions.restoreAuthenticationState.type,
    restoreVNZAuthenticationState
  )
}

export function* restoreVNZAuthenticationState(): SagaIterator<void> {
  const token = getTokensFromLocalStorage()

  if (token.isValid) {
    const { accessToken, expiryDate, expiresIn, refreshToken } = token

    yield put(
      VNZAuth.actions.setIsAuthenticated({
        accessToken,
        expiresIn,
        refreshToken,
        expiryDate: expiryDate.toISOString(),
      })
    )

    // refresh the token if expires soon
    const now = new Date()
    const isExpiredSoon = isBefore(
      expiryDate,
      addSeconds(now, REFRESH_BUFFER_IN_SECONDS)
    )

    if (isExpiredSoon) {
      yield put(VNZAuth.actions.refreshAuthenticationToken({}))
    }
  } else if (token.hasRequiredData) {
    yield put(VNZAuth.actions.refreshAuthenticationToken({}))
  }
}

export function* watchRefreshVNZAuthenticationToken() {
  yield takeLeading(
    VNZAuth.actions.refreshAuthenticationToken.type,
    refreshVNZAuthenticationToken
  )
}

const MAX_REFRESH_ATTEMPTS = 3
const REFRESH_RETRY_DELAY_IN_MS = 5_000

// a 400/401 from the refresh endpoint means the refresh token itself was
// rejected: retrying cannot change the answer
function isAuthRejection(error: unknown): boolean {
  const status = (error as ApiException)?.status
  return status === 400 || status === 401
}

function* refreshVNZAuthenticationToken(
  action: PayloadAction<RefreshAuthenticationTokenPayload>
): SagaIterator<void> {
  try {
    const api: PrinticularApi = yield call(getApi)

    const { refreshToken } = getTokensFromLocalStorage()

    if (!refreshToken) {
      yield put(
        VNZAuth.actions.setRefreshTokenError({
          refreshTokenError: "No refresh token available",
        })
      )
      return
    }

    let newTokenResult: VNZAuthTokenEntity | null = null
    for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        newTokenResult = yield call(api.refreshVerizonAccessToken, refreshToken)
        break
      } catch (error) {
        if (isAuthRejection(error) || attempt === MAX_REFRESH_ATTEMPTS) {
          throw error
        }
        yield delay(REFRESH_RETRY_DELAY_IN_MS)
      }
    }

    if (newTokenResult) {
      const { accessToken, refreshToken, expiresIn, expiryDt } = newTokenResult

      yield put(
        VNZAuth.actions.setIsAuthenticated({
          accessToken,
          refreshToken,
          expiresIn,
          expiryDate: expiryDt,
        })
      )
    }
  } catch (error) {
    logger.error("Error refreshing VNZ access token", error)

    if (isAuthRejection(error)) {
      // the refresh token is dead, the session cannot be recovered
      yield put(VNZAuth.actions.logout())
    } else {
      // transient failure (network, 5xx): keep the stored tokens so the
      // session can recover on the next attempt or page load
      yield put(
        VNZAuth.actions.setRefreshTokenError({
          refreshTokenError:
            (error as ApiException)?.message ?? "Unknown error",
        })
      )
    }
  }
}

export function* watchProcessVNZLogout() {
  yield takeEvery(VNZAuth.actions.logout.type, processVNZLogout)
}

function* processVNZLogout(): SagaIterator<void> {
  clearVNZTokensFromStorage()

  // free all decoded blob object URLs before dropping the entities
  const allImages: VNZImageEntity[] = yield select((state: RootState) =>
    vnzImagesSelectors.selectAll(state)
  )
  allImages.forEach(image => {
    if (image.thumbnailLocalUrl) {
      URL.revokeObjectURL(image.thumbnailLocalUrl)
    }
    if (image.fullImageLocalUrl) {
      URL.revokeObjectURL(image.fullImageLocalUrl)
    }
  })
  loadedThumbnailIds.length = 0

  // remove vnz images
  yield put(vnzImages.actions.removeAll())
}

export function* watchProcessVNZImages() {
  // takeLeading: while one fullview load is running, ignore further triggers.
  // Combined with the guard below this makes the (heavy, ~13MB) fetch idempotent
  // so remounts (e.g. the desktop/mobile modal swap on window resize) can't
  // kick off a duplicate fetch.
  yield takeLeading(startFetchingVNZCloudImages.type, processVNZImages)
}

function* processVNZImages(): SagaIterator<void> {
  try {
    // Skip if a load is already in flight or the images are already loaded.
    const isAlreadyLoading = yield select(
      (state: RootState) => state.VNZImages.isLoading
    )
    const alreadyLoadedCount = yield select((state: RootState) =>
      vnzImagesSelectors.selectTotal(state)
    )
    if (isAlreadyLoading || alreadyLoadedCount > 0) {
      return
    }

    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(vnzImages.actions.setHasError(false))
    yield put(vnzImages.actions.setIsLoading(true))
    const images: VNZImageEntity[] = yield retry(3, 3_000, () =>
      vnzService.fetchAllImages(accessToken)
    )

    // Insert progressively instead of one huge addMany. A single addMany of
    // thousands of entities is one long, blocking task (immer draft work plus a
    // deep auto-freeze of the whole collection), which is why the first render
    // felt far slower than later scroll renders. Paint the first chunk right
    // away, then append the rest a chunk per frame so the main thread stays
    // responsive and the grid appears immediately.
    const IMAGE_INSERT_CHUNK_SIZE = 500
    yield put(
      vnzImages.actions.addMany(images.slice(0, IMAGE_INSERT_CHUNK_SIZE))
    )
    yield put(vnzImages.actions.setIsLoading(false))

    for (
      let i = IMAGE_INSERT_CHUNK_SIZE;
      i < images.length;
      i += IMAGE_INSERT_CHUNK_SIZE
    ) {
      // yield to the browser so it can paint between chunks
      yield delay(250)
      yield put(
        vnzImages.actions.addMany(images.slice(i, i + IMAGE_INSERT_CHUNK_SIZE))
      )
    }
  } catch (error) {
    logger.error(error)
    yield put(vnzImages.actions.setHasError(true))
  }
}

export function* watchProcessVNZImageThumbnail() {
  yield takeEvery(
    vnzImages.actions.loadThumbnailForImage.type,
    processVNZImageThumbnail
  )
}

function* processVNZImageThumbnail(
  action: PayloadAction<LoadThumbnailForImagePayload>
): SagaIterator<void> {
  const imageId = action.payload.id
  const image: VNZImageEntity = yield select((state: RootState) =>
    vnzImagesSelectors.selectById(state, imageId)
  )

  try {
    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: { isLoadingThumbnail: true },
      })
    )

    const thumbnailLocalUrl = yield retry(3, 5_000, () =>
      vnzService.fetchThumbnail(accessToken, image)
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: true,
          thumbnailLocalUrl,
        },
      })
    )

    // LRU eviction of decoded thumbnail blobs to keep memory bounded.
    loadedThumbnailIds.push(image.id)
    if (loadedThumbnailIds.length > THUMBNAIL_CACHE_LIMIT) {
      const evictId = loadedThumbnailIds.shift()
      if (evictId != null && evictId !== image.id) {
        const evicted: VNZImageEntity = yield select((state: RootState) =>
          vnzImagesSelectors.selectById(state, evictId)
        )
        if (evicted?.thumbnailLocalUrl) {
          URL.revokeObjectURL(evicted.thumbnailLocalUrl)
        }
        yield put(
          vnzImages.actions.updateOne({
            id: evictId,
            changes: {
              isLoadingThumbnail: false,
              hasLoadedThumbnail: false,
              hasErrorLoadingThumbnail: false,
              thumbnailLocalUrl: undefined,
            },
          })
        )
      }
    }
  } catch (error) {
    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: false,
          hasErrorLoadingThumbnail: true,
        },
      })
    )
  }
}

export function* watchProcessVNZSessionImages() {
  yield takeLeading(
    vnzSessionImages.actions.processVNZSessionImages.type,
    processVNZSessionImages
  )
}

const IMAGE_PROCESSING_BATCH_SIZE = 6
function* processVNZSessionImages(): SagaIterator<void> {
  const isProcessing = yield select(
    (state: RootState) => state.VNZSessionImages.isProcessing
  )

  if (isProcessing) {
    return
  }

  // return stale session images when user hit retry
  const allSessionImages: VNZSessionImage[] = yield select((state: RootState) =>
    vnzSessionImagesSelectors.selectAll(state)
  )

  const sessionImages: VNZSessionImage[] = allSessionImages.filter(
    image => !image.hasBeenProcessed && seemsToBeAnImage(image.path)
  )

  if (sessionImages.length === 0) {
    return
  }

  const productId: EntityId = yield select(
    (state: RootState) => state.VNZSessionImages.printProduct
  )

  const couponCodes: string[] = yield select(
    (state: RootState) => state.VNZSessionImages.promoCodes
  )
  const hasCouponCodes = couponCodes && couponCodes.length > 0

  yield put(vnzSessionImages.actions.setIsProcessing(true))

  yield delay(500) // small delay to let the progress bar reach 0% before starting the modal

  const imageUrls: string[] = []
  const batchOfSessionImages: VNZSessionImage[][] = chunk(
    sessionImages,
    IMAGE_PROCESSING_BATCH_SIZE
  )

  for (const batch of batchOfSessionImages) {
    const results: string[] = yield all(
      batch.map(sessionImage => call(processVNZImageFromSession, sessionImage))
    )

    const successfulResults = results.filter(Boolean)
    imageUrls.push(...successfulResults)
  }

  yield put(
    actions.createLineItemForProductIdWithImageUrl({
      imageUrls,
      skipAutoScroll: true,
      productId,
    })
  )

  const processedSessionImages: VNZSessionImage[] = yield select(
    (state: RootState) => vnzSessionImagesSelectors.selectAll(state)
  )
  const hasError = processedSessionImages.some(image => image.hasError)
  const processedImageCount = processedSessionImages.filter(
    image => image.hasBeenProcessed
  ).length

  yield put(vnzSessionImages.actions.setHasError(hasError))
  yield put(vnzSessionImages.actions.setIsProcessing(false))
  yield put(vnzSessionImages.actions.setHasBeenProcessed(true))

  yield delay(1500)

  // apply coupon code if one is provided
  // if there are multiple codes, only the first one is applied
  if (hasCouponCodes && processedImageCount > 0) {
    const cart = yield call(getCurrentCart)
    yield call(applyCouponToCart, {
      cartId: cart.id,
      couponCode: couponCodes[0],
    })
  }

  if (hasError) {
    scrollTo("verizon-session")
  } else {
    yield call(scrollToCartOrStoreSearch)
  }
}

function* processVNZImageFromSession(
  vnzSessionImage: VNZSessionImage
): SagaIterator<string | undefined> {
  const vnzService: VNZService = yield call(getVnzService)
  const accessToken: string = yield select(
    (state: RootState) => state.VNZAuth.accessToken
  )

  try {
    yield put(
      vnzSessionImages.actions.updateOne({
        id: vnzSessionImage.id,
        changes: { isProcessing: true },
      })
    )

    const fullImageLocalUrl: string = yield call(() =>
      vnzService.fetchFullImageFromSession(accessToken, vnzSessionImage)
    )

    yield put(
      vnzSessionImages.actions.updateOne({
        id: vnzSessionImage.id,
        changes: {
          isProcessing: false,
          hasBeenProcessed: true,
          hasError: false,
        },
      })
    )

    return fullImageLocalUrl
  } catch (error) {
    logger.error("Error processing VNZ image", error)

    yield put(
      vnzSessionImages.actions.updateOne({
        id: vnzSessionImage.id,
        changes: {
          isProcessing: false,
          hasBeenProcessed: false,
          hasError: true,
        },
      })
    )
  }
}
